import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../../i18n/resolve-key';
import { Autocomplete, Box, Chip, IconButton, TextField } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import type { FilterControlProps, FilterOperator } from '../filters/filter-registry.js';
import {
  FILTER_OPERATORS,
  getOperatorsForType,
  registerFilterControl,
} from '../filters/filter-registry.js';
import { FilterOperatorMenu } from './FilterOperatorMenu.tsx';

type ReferenceFilterProps = FilterControlProps;

function normalizeSelected(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map(item =>
        item && typeof item === 'object'
          ? String((item as Record<string, unknown>).id ?? (item as Record<string, unknown>).value ?? '')
          : String(item ?? ''),
      )
      .filter(Boolean);
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const resolved = record.id ?? record.value;
    return resolved == null ? [] : [String(resolved)];
  }
  return value == null || value === '' ? [] : [String(value)];
}

export function ReferenceColumnFilter({
  column,
  value,
  onChange,
  disabled,
}: ReferenceFilterProps) {
  const { t } = useTranslation();
  const accessibleLabel = t(resolveTKey(column.labelKey));
  const allowedOperators = useMemo(() => {
    const candidates = getOperatorsForType('reference');
    const allowed = column.allowedFilterOperators;
    return allowed ? candidates.filter(operator => allowed.includes(operator.id)) : candidates;
  }, [column.allowedFilterOperators]);
  const defaultOperator = allowedOperators.find(operator => operator.id === 'equals')?.id
    ?? allowedOperators[0]?.id
    ?? 'equals';
  const [localOperator, setLocalOperator] = useState<FilterOperator>(value?.operator ?? defaultOperator);
  const [selectedValues, setSelectedValues] = useState<string[]>(normalizeSelected(value?.value));
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (value) {
      setLocalOperator(value.operator);
      setSelectedValues(normalizeSelected(value.value));
    } else {
      setLocalOperator(defaultOperator);
      setSelectedValues([]);
    }
  }, [defaultOperator, value]);

  const operatorDescriptor =
    FILTER_OPERATORS.find(operator => operator.id === localOperator) ?? allowedOperators[0];
  const isMulti = localOperator === 'in' || localOperator === 'notIn';
  const requiresValue = operatorDescriptor?.requiresValue !== false;

  const emit = (operator: FilterOperator, values: string[]) => {
    const descriptor = FILTER_OPERATORS.find(candidate => candidate.id === operator);
    if (descriptor?.requiresValue === false) {
      onChange({ operator, value: null });
      return;
    }
    if (values.length === 0) {
      onChange(null);
      return;
    }
    onChange({ operator, value: isMulti ? values : values[0] });
  };

  const handleOperatorChange = (operator: string) => {
    const nextOperator = operator as FilterOperator;
    const nextMulti = nextOperator === 'in' || nextOperator === 'notIn';
    const normalized = nextMulti ? selectedValues : selectedValues.slice(0, 1);
    setLocalOperator(nextOperator);
    setSelectedValues(normalized);
    const descriptor = FILTER_OPERATORS.find(candidate => candidate.id === nextOperator);
    if (descriptor?.requiresValue === false) onChange({ operator: nextOperator, value: null });
    else if (normalized.length > 0) onChange({ operator: nextOperator, value: nextMulti ? normalized : normalized[0] });
    else onChange(null);
  };

  const commitInput = () => {
    const next = inputValue.trim();
    if (!next) return;
    const values = isMulti
      ? Array.from(new Set([...selectedValues, next]))
      : [next];
    setSelectedValues(values);
    setInputValue('');
    emit(localOperator, values);
  };

  const handleClear = () => {
    setSelectedValues([]);
    setInputValue('');
    onChange(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {allowedOperators.length > 1 && (
        <FilterOperatorMenu
          column={column}
          currentOperator={localOperator}
          onChange={handleOperatorChange}
          allowedOperators={allowedOperators}
          disabled={disabled}
        />
      )}
      {requiresValue && (
        <Autocomplete<string, boolean, false, true>
          multiple={isMulti}
          freeSolo
          options={[]}
          value={isMulti ? selectedValues : (selectedValues[0] ?? null)}
          inputValue={inputValue}
          onInputChange={(_event, nextInput) => setInputValue(nextInput)}
          onChange={(_event, nextValue) => {
            const values = normalizeSelected(nextValue);
            setSelectedValues(values);
            emit(localOperator, values);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter' && inputValue.trim()) {
              event.preventDefault();
              commitInput();
            }
          }}
          onBlur={commitInput}
          disabled={disabled}
          fullWidth
          size="small"
          clearOnBlur={false}
          renderValue={(values, getItemProps) =>
            (Array.isArray(values) ? values : [values]).map((raw, index) => (
              <Chip
                {...getItemProps({ index })}
                key={String(raw)}
                label={String(raw)}
                size="small"
                sx={{ maxWidth: 180 }}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              placeholder={accessibleLabel}
              sx={{ minWidth: 0 }}
              slotProps={{
                ...params.slotProps,
                htmlInput: {
                  ...params.slotProps?.htmlInput,
                  'aria-label': accessibleLabel,
                },
              }}
            />
          )}
        />
      )}
      {(selectedValues.length > 0 || !requiresValue) && (
        <IconButton
          size="small"
          onClick={handleClear}
          disabled={disabled}
          aria-label={t('common:clear')}
          sx={{ flexShrink: 0 }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

function createReferenceFilter() {
  return React.memo(ReferenceColumnFilter);
}

registerFilterControl('reference', () => createReferenceFilter());
